/**
 * Swipe View — TikTok playback adapter chain (SMASH-1853).
 *
 * Implements the per-plugin seam defined by the Swipe View experience spec
 * §9.1 (docs/technical-designs/swipe-view-experience-spec.md) and AgDR-0500.
 * This is the ONLY place that knows about TikTok's real post shape
 * (`video_url`, `cover_image_url`, `id`, `share_url`, `video_description`).
 * The shell (SwipeSlot.jsx / SwipeView.jsx) knows nothing about TikTok.
 *
 * Contract (normative, spec §9.1):
 *   factory  = { name, transport, canPlay(post) -> bool,
 *                create(post, ctx) -> instance }
 *   instance = { name, caps: { mute, playPause, progress },
 *                play({muted}), pause(), setMuted(bool), isPlaying(),
 *                progress() -> {current,duration}|null, restart(), destroy(),
 *                recycle?(post) }
 *   ctx = { stage, emit(kind, detail), preload? }
 *                                        kind: ready|playing|stalled|ended|error
 *
 * `transport` and `ctx.preload` are the two additions SMASH-1979 needed, and
 * both exist because §4.4's adjacent pre-warm is scoped by a property of the
 * TIER rather than of the shell: "Pre-warm applies only to a tier whose
 * transport is a direct HTMLMediaElement." The shell cannot answer that question
 * by inspecting an adapter — it has no handle on the element — so the adapter
 * declares it. This is the same seam for all four Swipe View ports, not a
 * per-plugin UI quirk, which is what makes widening it the right layer here
 * (contrast the round-2 attempt to route the terminal tier's link through the
 * seam, reverted for exactly that reason).
 *
 * Kept as plain, framework-agnostic JS (not JSX) deliberately: the adapter
 * instance API is inherently imperative (play()/pause()/destroy() on a real
 * media element), which is what the spec itself specifies — the React port
 * is the shell around this, not this layer. `ctx.stage` is a bare DOM node
 * owned by a React-rendered slot (see SwipeSlot.jsx); the adapter appends
 * real DOM directly into it, then tears it down on destroy()/recycle().
 */
import { __ } from '@wordpress/i18n';

export const NO_CAPS = { mute: false, playPause: false, progress: false };

/**
 * A tier's TRANSPORT (spec §4.4, "Transport, not tier index"). Every factory
 * below declares one, and the pre-warm scope is derived from it rather than from
 * a tier index — the spec is explicit that scoping by index "would permit
 * precisely the case its rationale forbids" (YouTube's tier 1 is an iframe).
 */
export const TRANSPORT = {
    /** A direct `HTMLMediaElement` — the only transport a pre-warm may touch. */
    MEDIA: 'media',
    /**
     * A cross-origin frame. Never pre-warmed, for three compounding reasons the
     * spec gives, all properties of the transport: it is heavy, it exposes no
     * playback API to warm against (the frame's `load` event is all the page can
     * observe — §5.2's accepted limitation), and it is a THIRD-PARTY load
     * carrying its own consent semantics, so a frame fetched for a slide the
     * visitor never reaches is a request made on their behalf for content they
     * never asked to see. It also falls out of the adapter shape: for an
     * iframe-backed player there is no `create()` that does not load.
     */
    IFRAME: 'iframe',
    /** Nothing plays: a poster-plus-link presentation. Nothing to warm. */
    STATIC: 'static',
};

/**
 * Whether a tier may be pre-warmed (spec §4.4 Level A/B).
 *
 * Fail-safe by construction: anything that is not explicitly `MEDIA` — an
 * unknown transport, a factory that forgot to declare one, a null — answers
 * `false`. That is the same direction §4.4 takes on the connection signal
 * ("absence of the signal is not permission"), and it means a future tier added
 * without a transport is silently excluded from pre-warm rather than silently
 * included in it.
 */
export function isPrewarmableTransport(factory) {
    return !!factory && factory.transport === TRANSPORT.MEDIA;
}

/**
 * §12 security: only http(s) may reach an href built from remote post data.
 */
export function safeUrl(u) {
    if (!u) return '';
    return /^https?:\/\//i.test(String(u)) ? String(u) : '';
}

/**
 * §12 security: media/poster src may also be same-origin relative (local
 * cover images), but any other scheme (javascript:, data:, blob:, …) is
 * rejected the same way a full URL would be.
 */
export function safeMediaUrl(u) {
    const s = String(u || '');
    if (!s) return '';
    if (/^https?:\/\//i.test(s)) return s;
    return /^[a-z][a-z0-9+.-]*:/i.test(s) ? '' : s;
}

/**
 * §12 security: "any identifier interpolated into an embed URL MUST be
 * validated against an expected shape before use, not concatenated raw."
 * TikTok post ids are numeric (findings.md §3); reject anything else rather
 * than trust the relay's own `embed_link` field. Mirrors the existing
 * `https://www.tiktok.com/embed/v2/${post.id}` construction already used by
 * Modal.jsx, so this is not a new URL shape — just a validated one.
 */
export function buildTikTokEmbedUrl(post) {
    const id = post && post.id;
    if (id === undefined || id === null) return '';
    const idStr = String(id);
    if (!/^\d+$/.test(idStr)) return '';
    return `https://www.tiktok.com/embed/v2/${idStr}`;
}

/**
 * Shared play-with-fallback for the native <video> tier (Maintainer review,
 * SMASH-1853 — sound-on-open). Spec §4.2: "If unmuted playback is refused
 * for want of a qualifying gesture, the viewer MUST fall back to muted and
 * continue... MUST NOT stall on a rejected play() promise." This is the ONE
 * place that logic lives; `play()`, `restart()` and `setMuted()` all route
 * through it so the fallback can't silently regress in one call site while
 * staying fixed in another — which is exactly what happened before this
 * pass: `restart()` (the loop-at-end path, spec §4.3) called
 * `video.play().catch(() => {})` and swallowed a rejected unmuted retry
 * without ever muting or emitting `autoplay-blocked`, so a loop-triggered
 * rejection left the shell believing playback was fine while the element
 * had in fact stopped (a rejected play() leaves the video paused).
 */
function attemptPlay(video, ctx, muted) {
    video.muted = muted;
    const p = video.play();
    if (!p || !p.catch) return;
    p.catch(() => {
        // §4.2: unmuted autoplay refused for want of a gesture — fall back
        // to muted and continue, never stall on the rejected promise.
        if (!muted) {
            video.muted = true;
            ctx.emit('autoplay-blocked', { intended: 'unmuted' });
            video.play().catch(() => {
                ctx.emit('autoplay-blocked', { intended: 'muted' });
            });
        } else {
            ctx.emit('autoplay-blocked', { intended: 'muted' });
        }
    });
}

/* --------------------------------------------------- tier 1: native <video> */
/**
 * TikTok's cached `video_url` (signed CDN URL, ~2-day TTL, no refresh —
 * findings.md §3). Swipe autoplay MUST be muted + playsInline (spec §4.1);
 * today's Video.jsx never sets either, which is the exact gap this adapter
 * closes for the swipe surface without touching the existing lightbox player.
 */
/** 9:16, with the reference's own 4% tolerance either side of it. */
const PORTRAIT_RATIO = 9 / 16;
const RATIO_TOLERANCE = 1.04;

/**
 * Decide `contain` vs `cover` for a loaded video, and build the blurred poster
 * backdrop when it letterboxes (rule 1.14).
 *
 * Exported for the suites: this is arithmetic over two numbers plus one DOM
 * insert, and testing it through a real `loadedmetadata` event in jsdom — where
 * `videoWidth` is always 0 — would prove nothing.
 */
export function applyAspectFit(video, stage, posterUrl) {
    const w = video.videoWidth;
    const h = video.videoHeight;
    // No metadata yet, or a source that reports nothing: leave the UA default
    // alone rather than guessing at a crop.
    if (!w || !h) return null;

    const wider = w / h > PORTRAIT_RATIO * RATIO_TOLERANCE;
    video.style.objectFit = wider ? 'contain' : 'cover';
    video.setAttribute('data-fit', wider ? 'contain' : 'cover');

    // Only the letterboxed case has black to fill, and only a post with a cover
    // image has anything to fill it with. Without a poster the frame's own black
    // is the honest fallback — which is the common case on Instagram, where most
    // cached rows have no poster, and worth knowing before reading a screenshot.
    if (!wider || !posterUrl || !stage) return null;

    const existing = stage.querySelector('.sbtt-swipeview-backdrop');
    if (existing) return existing;

    const backdrop = document.createElement('div');
    backdrop.className = 'sbtt-swipeview-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    // The URL is set as a style rather than an `<img>` src so it cannot be
    // announced or focused, and it goes through the same `safeMediaUrl` the
    // poster attribute already used — the caller passes the checked value.
    backdrop.style.backgroundImage = `url("${posterUrl.replace(/"/g, '%22')}")`;
    // FIRST child, so it paints under the video without needing a z-index on
    // either. The stage holds exactly the media plus this.
    stage.insertBefore(backdrop, stage.firstChild);
    return backdrop;
}

export function nativeVideoAdapter() {
    return {
        name: 'native-video',
        transport: TRANSPORT.MEDIA,
        canPlay(post) {
            return !!safeMediaUrl(post && post.video_url);
        },
        create(post, ctx) {
            const video = document.createElement('video');
            video.src = safeMediaUrl(post.video_url);
            // `let`, not `const`: `recycle()` below retargets it so the blurred
            // letterbox backdrop is built from the NEW post's cover rather than
            // the one this element was created for.
            let poster = safeMediaUrl(post.cover_image_url || post.local_cover_image_url);
            if (poster) video.poster = poster;
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', ''); // iOS Safari: required or playback goes fullscreen.
            // Blocker 3 (SMASH-1853 review): no `crossorigin` here. Nothing in
            // this adapter needs a CORS-mode fetch (no canvas, no <track>, no
            // captured frames), it was the only `crossorigin` in the repo, and
            // Video.jsx plays these same signed CDN URLs without one. If the
            // CDN omits ACAO on the signed URL, adding it would fail the load
            // and silently degrade every slide to the embed tier.
            /*
             * §4.4 preload policy, in the spec's CURRENT normative wording: the
             * active slide MAY buffer aggressively, and a neighbouring slide
             * MUST NOT buffer its payload **by default** — metadata is the
             * default ceiling, and Level B of the adjacent pre-warm is the only
             * sanctioned exception.
             *
             * That wording is a change (SMASH-1979): the older rule read
             * "neighbours MUST NOT", full stop, which left no room for the
             * Level B a plausibly-fast connection now earns. The comment said
             * so for a while after the spec stopped saying so, which is exactly
             * the kind of drift that gets read as authority later.
             *
             * `ctx.preload` is how the shell states which level applies, and it
             * is only ever set on a pre-warm — SwipeSlot.jsx decides via
             * swipePrewarm.js. `'metadata'` is the correct default for every
             * other path: a slide is assigned before it is ever the active one,
             * and `play()` below bumps it to `'auto'` on activation.
             */
            video.preload = ctx.preload || 'metadata';
            const caption = (post && post.video_description) || __('TikTok video', 'sb-customizer');
            video.setAttribute('aria-label', caption);

            video.addEventListener('error', () => ctx.emit('error', { reason: 'media-load-failed' }));
            /*
             * ── Aspect handling (rule 1.14) ─────────────────────────────────
             *
             * Short-form video is 9:16 essentially always, but a feed contains
             * the occasional landscape or square post, and until now every one
             * of them was letterboxed onto flat black by the UA's own
             * `object-fit: contain`.
             *
             * The design's answer for the WIDER-than-9:16 case is a blurred,
             * scaled, dimmed copy of the post's own cover image behind the
             * letterboxed video — "the first frame of the video is scaled up and
             * then it's blurred as the background, so that way it kind of feels
             * more consistent rather than having it black". He gave that its own
             * 35-second segment with a static frame, so he wanted it noticed.
             *
             * TALLER than 9:16 is `cover` — cropped, no backdrop — which is what
             * his build does. He never mentions that case, and it is recorded as
             * an open question (Q5) rather than treated as settled: real feeds
             * do contain taller-than-9:16 uploads.
             *
             * The 1.04 tolerance is his too, and it earns its place: a source a
             * fraction off 9:16 (a re-encode, a rounded crop) would otherwise
             * take the letterbox path and grow a blurred frame for a band a few
             * pixels tall.
             *
             * Measured HERE rather than in the shell, at `loadedmetadata`, because
             * this is the only place the real `videoWidth`/`videoHeight` are
             * known — the post payload carries no dimensions.
             */
            video.addEventListener('loadedmetadata', () => {
                applyAspectFit(video, ctx.stage, poster);
                ctx.emit('ready');
            });
            video.addEventListener('playing', () => ctx.emit('playing'));
            video.addEventListener('waiting', () => ctx.emit('stalled'));
            video.addEventListener('ended', () => ctx.emit('ended'));

            ctx.stage.appendChild(video);

            return {
                name: 'native-video',
                caps: { mute: true, playPause: true, progress: true },
                play(opts) {
                    const muted = !opts || opts.muted !== false;
                    video.preload = 'auto';
                    attemptPlay(video, ctx, muted);
                },
                pause() {
                    video.pause();
                },
                setMuted(muted) {
                    // Explicit user action (the rail's sound toggle, or the
                    // "Tap to unmute" pill) — muting never needs a play()
                    // call; unmuting does, and routes through the SAME
                    // fallback as every other activation so a rejected
                    // retry still degrades to muted + re-arms the pill
                    // instead of leaving the element silently paused.
                    if (muted) {
                        video.muted = true;
                    } else {
                        attemptPlay(video, ctx, false);
                    }
                },
                isPlaying() {
                    return !video.paused && !video.ended;
                },
                // Rex review (SMASH-1853): the shell must render the REAL
                // mute state, not the `unmuted` intent it asked for — a
                // rejected attemptPlay() retry can leave the element muted
                // even though the intent never flipped (that per-attempt
                // fallback is deliberate, spec §4.2). Sampled by SwipeView's
                // 250ms poll, same cadence as caps/playing/progress.
                isMuted() {
                    return video.muted;
                },
                progress() {
                    return { current: video.currentTime, duration: video.duration || 0 };
                },
                restart() {
                    // §4.3 loop-at-end — spec calls this out by name as an
                    // activation path that can land outside a user gesture;
                    // preserve whatever mute state is currently in effect
                    // rather than forcing either value.
                    video.currentTime = 0;
                    attemptPlay(video, ctx, video.muted);
                },
                /*
                 * Bank this ELEMENT's own autoplay grant, while a user gesture
                 * is still in scope (SMASH-1853 follow-up, iOS unmute
                 * persistence).
                 *
                 * On WebKit the permission to play AUDIBLY is granted per media
                 * element and banked by a successful gesture-backed `play()`.
                 * An element that has once played under a gesture may later be
                 * played programmatically; a freshly created one has nothing
                 * banked however good the gesture was. Chrome and Firefox gate
                 * per ORIGIN, so a fresh element inherits the page's permission
                 * and none of this is visible in desktop testing — which is why
                 * four independently written viewers shipped the same gap.
                 *
                 * §4.4's pre-warm rule is "never audible, never left playing",
                 * and both halves are enforced here rather than promised: the
                 * element is muted BEFORE the call, and paused again the moment
                 * playback actually starts.
                 *
                 * An element already playing is left alone. Priming it would
                 * mean pausing the slide the visitor is watching, and it has a
                 * banked grant by definition.
                 *
                 * `preload` is deliberately NOT raised. A muted play on a
                 * `preload="metadata"` element fetches enough to start and is
                 * stopped immediately, which is a real (small) cost over Level
                 * A's pure metadata read — the trade §4.4's rule was rewritten
                 * to allow, since the alternative is no audio at all on iOS
                 * after the first swipe.
                 */
                prime() {
                    if (!video.paused) return false;
                    video.muted = true;
                    const p = video.play();
                    const stop = () => video.pause();
                    if (p && p.then) p.then(stop, () => {});
                    else stop();
                    return true;
                },
                /*
                 * Swap the SOURCE and keep the element (SMASH-1853 follow-up).
                 *
                 * The slot pool's `key` is stable, but the `<video>` inside it
                 * used to be destroyed and rebuilt whenever the slot's post
                 * changed — so every swipe handed the visitor an element with no
                 * banked grant, and `prime()` above would have had nothing
                 * durable to bank into. Reusing the element is what makes the
                 * grant last a session.
                 *
                 * No explicit `load()`: assigning `src` invokes the resource
                 * selection algorithm on its own, and an explicit `load()` is
                 * one more media-load state reset than necessary on an element
                 * whose whole value here is its continuity.
                 *
                 * Muted and back to `metadata` because a recycled slot is a
                 * NEIGHBOUR until it activates — §4.4's Level A shape, the same
                 * state `create()` leaves a warm element in. Activation raises
                 * both (see `play()`).
                 */
                recycle(nextPost) {
                    const nextSrc = safeMediaUrl(nextPost && nextPost.video_url);
                    // The caller only recycles when a fresh chain walk lands on
                    // this same tier, which requires canPlay() — so an empty URL
                    // here would mean the gate is broken, and silently blanking
                    // the element would hide that. Refuse instead; the caller
                    // rebuilds.
                    if (!nextSrc) return false;
                    video.pause();
                    video.muted = true;
                    video.preload = 'metadata';
                    video.src = nextSrc;
                    poster = safeMediaUrl(nextPost.cover_image_url || nextPost.local_cover_image_url);
                    if (poster) video.poster = poster;
                    else video.removeAttribute('poster');
                    /*
                     * The fit decision belongs to the SOURCE, so the previous
                     * post's answer has to go with it — otherwise a portrait
                     * clip inherits `contain` from a landscape one and paints
                     * bars it does not need. `loadedmetadata` fires again for
                     * the new source and re-decides.
                     */
                    video.style.objectFit = '';
                    video.removeAttribute('data-fit');
                    const stale = ctx.stage && ctx.stage.querySelector('.sbtt-swipeview-backdrop');
                    if (stale) stale.remove();
                    return true;
                },
                destroy() {
                    video.pause();
                    video.removeAttribute('src');
                    video.load();
                    video.remove();
                },
            };
        },
    };
}

/* --------------------------------------- tier 2: platform embed (tiktok.com) */
/**
 * `tiktok.com/embed/v2/{id}` — the same fallback already used by the classic
 * lightbox (Modal.jsx) when a post has no `video_url`. Exposes no playback
 * API and no failure signal (spec §5.2's accepted limitation): the frame's
 * `load` event fires even for an error document, so it is treated as
 * "playing" optimistically. Recycled in place per §4.4 — the iframe element
 * is never removed/re-added, only its `src` changes.
 */
export function platformEmbedAdapter() {
    return {
        name: 'platform-embed',
        transport: TRANSPORT.IFRAME,
        canPlay(post) {
            return !!buildTikTokEmbedUrl(post);
        },
        create(post, ctx) {
            const iframe = document.createElement('iframe');
            iframe.src = buildTikTokEmbedUrl(post);
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
            iframe.referrerPolicy = 'strict-origin-when-cross-origin';
            iframe.setAttribute('scrolling', 'no');
            iframe.setAttribute('title', (post && post.video_description) || __('TikTok video embed', 'sb-customizer'));
            let loaded = false;
            iframe.addEventListener('load', () => {
                loaded = true;
                ctx.emit('ready');
                ctx.emit('playing'); // optimistic — see module docblock + spec §5.2/§9.1.
            });
            ctx.stage.appendChild(iframe);

            return {
                name: 'platform-embed',
                caps: { mute: false, playPause: false, progress: false },
                play() {},
                pause() {},
                setMuted() {},
                isPlaying() {
                    return loaded;
                },
                // No mute control on this tier (caps.mute is false, so the
                // shell's toggle stays disabled regardless) — `false` is
                // advisory only, matching the platform's own embed default.
                isMuted() {
                    return false;
                },
                progress() {
                    return null;
                },
                restart() {},
                // §4.4: iframe-backed players MUST be recycled in place, never
                // reparented — swapping `src` keeps the same DOM node alive.
                recycle(nextPost) {
                    const nextSrc = buildTikTokEmbedUrl(nextPost);
                    if (!nextSrc) return;
                    loaded = false;
                    iframe.src = nextSrc;
                    iframe.setAttribute('title', (nextPost && nextPost.video_description) || __('TikTok video embed', 'sb-customizer'));
                },
                destroy() {
                    iframe.src = 'about:blank';
                    iframe.remove();
                },
            };
        },
    };
}

/* --------------------------------------------- tier 3 (terminal): poster+link */
/**
 * Nothing plays here; it is a valid presentation (also the GDPR-no-consent
 * shape, spec §5.3). Caption/title/link are rendered as text nodes only —
 * never innerHTML — per §12. Reports itself as "playing" immediately so the
 * shell's start watchdog does not fire on a tier with nowhere left to escalate.
 *
 * Blocker 1 (PR #191 review): the "Open on TikTok" link is real DOM inside
 * `ctx.stage` — i.e. inside a slide, inside the track — same as every other
 * tier's content. See SwipeView.css's `.sbtt-swipeview-track` rule for how
 * it is made pointer-reachable despite the gesture layer sitting on top of
 * everything else on the stage; the adapter contract itself did not need to
 * change (a round-2 attempt moved this into the shell's chrome layer
 * instead — reverted: the simpler stacking fix works, and widening the
 * §9.1 adapter seam for one plugin's UI quirk was the wrong layer for the
 * fix, per the shared-shell design across all four Swipe View ports).
 */
export function posterLinkAdapter() {
    return {
        name: 'poster-link',
        transport: TRANSPORT.STATIC,
        canPlay() {
            return true;
        },
        create(post, ctx) {
            const wrap = document.createElement('div');
            wrap.className = 'sbtt-swipeview-fallback';

            const poster = safeMediaUrl(post && (post.cover_image_url || post.local_cover_image_url));
            if (poster) {
                const img = document.createElement('img');
                img.className = 'sbtt-swipeview-fallback-poster';
                img.src = poster;
                img.alt = ''; // decorative — caption is rendered by the viewer's own chrome as text.
                wrap.appendChild(img);
            }

            const msg = document.createElement('p');
            msg.className = 'sbtt-swipeview-fallback-message';
            msg.textContent = __('This video can’t be played here.', 'sb-customizer');

            const href = safeUrl(post && post.share_url);
            const link = document.createElement('a');
            link.className = 'sbtt-swipeview-fallback-link';
            link.href = href || '#';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.textContent = __('Open on TikTok', 'sb-customizer');

            wrap.appendChild(msg);
            wrap.appendChild(link);
            ctx.stage.appendChild(wrap);

            ctx.emit('playing');

            return {
                name: 'poster-link',
                caps: { mute: false, playPause: false, progress: false },
                play() {},
                pause() {},
                setMuted() {},
                isPlaying() {
                    return true;
                },
                // No audio at all on this tier (caps.mute is false) — `true`
                // is the more honest advisory default (nothing is playing).
                isMuted() {
                    return true;
                },
                progress() {
                    return null;
                },
                restart() {},
                destroy() {
                    wrap.remove();
                },
            };
        },
    };
}

/**
 * The ordered TikTok fallback chain (spec §5.1): native video -> platform
 * embed -> poster+link. A fresh chain per SwipeView instance keeps adapter
 * state (e.g. the embed's `loaded` flag) scoped to one open/close cycle.
 */
export function createTikTokAdapterChain() {
    return [nativeVideoAdapter(), platformEmbedAdapter(), posterLinkAdapter()];
}
